/**
 * Photos upload the moment they are taken (PLAN §6 step 3): shrunk in the browser, then put at
 * `inspections/<vehicle_id>/<client_ref>/<name>.jpg` with upsert, so a retry overwrites rather than
 * duplicates. Each photo has a status for its dot and keeps its shrunk copy in memory for Retry.
 *
 * Only an uploaded photo's URL reaches the draft (through `onUploaded`); the in-flight state lives
 * here, so a photo still uploading at a reload is lost — the v1 cost the plan accepts.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { downscaleImage, UnreadableImageError } from '@/lib/downscaleImage';
import type { PhotoSlot } from '@/lib/inspections';
import { uploadInspectionPhoto } from '../inspectionApi';

export type PhotoTarget = { slot: PhotoSlot } | { item: string };
export type UploadStatus = 'preparing' | 'uploading' | 'done' | 'error' | 'unreadable';

export interface PhotoUpload {
    /** The object's name in the visit's folder; unique within a draft, so it keys the upload too. */
    name: string;
    target: PhotoTarget;
    status: UploadStatus;
    /** A local object URL of the shrunk photo, shown until the page is left. */
    previewUrl: string | null;
    url: string | null;
    error: string | null;
}

interface Options {
    vehicleId: number;
    clientRef: string;
    onUploaded: (target: PhotoTarget, url: string) => void;
}

export function usePhotoUploads({ vehicleId, clientRef, onUploaded }: Options) {
    const [uploads, setUploads] = useState<Record<string, PhotoUpload>>({});
    const uploadsRef = useRef(uploads);
    uploadsRef.current = uploads;
    const blobs = useRef(new Map<string, Blob>());
    const previews = useRef(new Set<string>());
    const onUploadedRef = useRef(onUploaded);
    onUploadedRef.current = onUploaded;

    useEffect(() => {
        const urls = previews.current;
        return () => {
            for (const url of urls) URL.revokeObjectURL(url);
            urls.clear();
        };
    }, []);

    const patch = useCallback((name: string, next: Partial<PhotoUpload>) => {
        setUploads((prev) => (prev[name] ? { ...prev, [name]: { ...prev[name], ...next } } : prev));
    }, []);

    const send = useCallback(
        async (name: string, target: PhotoTarget, blob: Blob) => {
            patch(name, { status: 'uploading', error: null });
            try {
                const url = await uploadInspectionPhoto(vehicleId, clientRef, name, blob);
                patch(name, { status: 'done', url });
                // A photo removed while it was still uploading does not come back into the draft.
                if (uploadsRef.current[name]) onUploadedRef.current(target, url);
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                patch(name, { status: 'error', error: message || 'The upload did not finish.' });
            }
        },
        [vehicleId, clientRef, patch],
    );

    /** Shrink and upload a picked file. A retake of a slot reuses its name and replaces it. */
    const start = useCallback(
        async (name: string, target: PhotoTarget, file: File) => {
            const old = uploadsRef.current[name]?.previewUrl;
            if (old) {
                URL.revokeObjectURL(old);
                previews.current.delete(old);
            }
            setUploads((prev) => ({ ...prev, [name]: { name, target, status: 'preparing', previewUrl: null, url: null, error: null } }));
            let blob: Blob;
            try {
                blob = await downscaleImage(file);
            } catch (error) {
                patch(name, {
                    status: error instanceof UnreadableImageError ? 'unreadable' : 'error',
                    error: error instanceof Error ? error.message : 'The photo could not be prepared.',
                });
                return;
            }
            blobs.current.set(name, blob);
            const previewUrl = URL.createObjectURL(blob);
            previews.current.add(previewUrl);
            patch(name, { previewUrl });
            await send(name, target, blob);
        },
        [patch, send],
    );

    const retry = useCallback(
        (name: string) => {
            const blob = blobs.current.get(name);
            const entry = uploadsRef.current[name];
            if (blob && entry) void send(name, entry.target, blob);
        },
        [send],
    );

    /** Forget an upload the inspector removed or gave up on. The object, if any, stays in the bucket. */
    const dismiss = useCallback((name: string) => {
        const gone = uploadsRef.current[name];
        if (!gone) return;
        if (gone.previewUrl) {
            URL.revokeObjectURL(gone.previewUrl);
            previews.current.delete(gone.previewUrl);
        }
        blobs.current.delete(name);
        setUploads((prev) => {
            if (!prev[name]) return prev;
            const rest = { ...prev };
            delete rest[name];
            return rest;
        });
    }, []);

    const busy = Object.values(uploads).some((u) => u.status === 'preparing' || u.status === 'uploading');

    return { uploads, start, retry, dismiss, busy };
}

export type PhotoUploads = ReturnType<typeof usePhotoUploads>;
